/**
 * Store pour la gestion des utilisateurs
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';
import Str from '@classes/Str';

export const useUserStore = defineStore('user', (() => {

    // State de la liste des utilisateurs groupés par type (total et users)
    const userGroupList = ref({});

    // State des utilisateurs chargés
    const users = ref([]);

    /**
     * Retourne les données de liste du type en paramètre (total et users)
     * @param string userType Type d'utilisateur
     * @return object
     */
    const getUserListDataOfType = computed(() => {

        return ((userType) => {
            return (userGroupList.value[userType] || {});
        });
    });

    /**
     * Retourne l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @return object|null
     */
    const getUserById = computed(() => {
        return ((userId) => {
            const index = users.value.findIndex((user) => user.id === userId);
            return (index === -1) ? null : users.value[index];
        });
    });

    /**
     * Chargement des utilisateurs en fonction du type en paramètre
     * @param userType
     * @param object params Paramètres pour la requête Ajax
     * @return Promise
     */
    const loadUserListOfType = ((userType, params) => {

        const userTypeCamel = new Str(userType.toLowerCase()).toCamelCase('_');
        const userTypeSlug = new Str(userTypeCamel).toSnakeCase();
        const path = '/api/admin/users/:userType'.replace(':userType', userTypeSlug);

        return axios.get(path, {
            params: params
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.data) {

                if(params.offset === 0) {
                    resetUserList(userType);
                }

                updateUserList(userType, response.data.data);
                return Promise.resolve();
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Chargement de l'utilisateur dont l'identifiant est en paramètre
     * @param int userId
     * @param string userTypeCamelCase Type d'utilisateur au format camelCase
     * @param array withParam Données supplémentaire à charger
     * @return Promise
     */
    const loadUserById = ((userId, userTypeCamelCase, withParam = []) => {

        let requiredLoad = false;
        const existingUser = getUserById.value(userId);
        if(existingUser === null) {
            requiredLoad = true;
        } else {
            requiredLoad = (withParam.filter((field) => existingUser[field] === undefined).length > 0);
        }

        if(! requiredLoad) {
            return Promise.resolve(existingUser);
        }

        const userTypeRouteParam = new Str(userTypeCamelCase).toSnakeCase();
        const path = '/api/admin/users/:userType/:userId'
            .replace(':userType', userTypeRouteParam)
            .replace(':userId', userId)
        ;

        return axios.get(path, {
            params: {
                with: withParam.join(',')
            }
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateUser(response.data.user);
                return Promise.resolve(response.data.user);
            } else {
                return Promise.reject();
            }
        }).catch(e => {

        });

    });

    /**
     * Ajoute le nombre de crédits demandé pour l'utilisateur en paramètre
     * @param object user
     * @param int creditsToAdd Nombre de crédits à ajouter
     * @return Promise
     */
    const addCredits = ((user, creditsToAdd) => {

        const userTypeCamel = new Str(user.type.toLowerCase()).toCamelCase('_');
        const userTypeSlug = new Str(userTypeCamel).toSnakeCase();

        const path = '/api/admin/users/:userType/:userId/add-credits'
            .replace(':userType', userTypeSlug)
            .replace(':userId', user.id)
        ;

        return axios.post(path, {
            credits: creditsToAdd
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateUser(response.data.user);
                return Promise.resolve(response.data.user);
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Modifie le nombre de crédits de l'utilisateur en paramètre
     * @param object user
     * @param int newCredits Nombre de crédits à ajouter
     * @return Promise
     */
    const changeCredits = ((user, newCredits) => {

        const userTypeCamel = new Str(user.type.toLowerCase()).toCamelCase('_');
        const userTypeSlug = new Str(userTypeCamel).toSnakeCase();

        const path = '/api/admin/users/:userType/:userId/change-credits'
            .replace(':userType', userTypeSlug)
            .replace(':userId', user.id)
        ;

        return axios.post(path, {
            credits: newCredits
        }).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateUser(response.data.user);
                return Promise.resolve(response.data.user);
            }
            return Promise.reject();
        });

    });

    /**
     * Modifie le statut de l'agent
     * Si le statut était ENABLED, il devient DISABLED
     * Si le status était DISABLED, il devient ENABLED
     * @param object user
     * @return Promise
     */
    const toggleAgentStatus = ((user) => {

        const userTypeCamel = new Str(user.type.toLowerCase()).toCamelCase('_');
        const userTypeSlug = new Str(userTypeCamel).toSnakeCase();

        const path = '/api/admin/users/:userType/:userId/toggle-agent-status'
            .replace(':userType', userTypeSlug)
            .replace(':userId', user.id)
        ;

        return axios.post(path, {}).then((response) => {
            if(response && response.data && response.data.success && response.data.user) {
                updateUser(response.data.user);
                return Promise.resolve(response.data.user);
            } else {
                return Promise.reject();
            }
        });

    });

    /**
     * Mise à jour des données de l'utilisateur dans les données chargées
     * @param object user
     * @return void
     */
    const updateUser = ((user) => {

        const index = users.value.findIndex((current) => current.id === user.id);
        if(index === -1) {
            users.value.push(user);
        } else {
            users.value[index] = { ...users.value[index], ...user };
        }

        // Gestion de la liste
        const currentDataList = getUserListDataOfType.value(user.type);
        if(currentDataList.total === undefined) {
            return;
        }

        // Fusion de l'utilisateur dans la liste où il se trouve
        const newUserList = currentDataList.users;
        const indexFromList = newUserList.findIndex((currentUser) => currentUser.id === user.id);
        if(indexFromList !== -1) {
            const currentUser = userGroupList.value[user.type].users[indexFromList];
            userGroupList.value[user.type].users[indexFromList] = { ...currentUser, ...user };
        }

    });

    /**
     * Supprimer un utilisateur du store
     * @param object user L'utilisateur à supprimer
     * @return void
     */
    const removeUser = ((user) => {

        // Suppression des utilisateurs déjà chargés
        const indexFromLoading = users.value.findIndex((current) => current.id === user.id);
        users.value.splice(indexFromLoading, 1);

        //
        const currentDataList = getUserListDataOfType.value(user.type);
        if(currentDataList.total === undefined) {
            return;
        }

        // Suppression de la liste
        const newUserList = currentDataList.users;
        const indexFromList = newUserList.findIndex((currentUser) => currentUser.id === user.id);
        if(indexFromList !== -1) {
            newUserList.splice(indexFromList, 1);
            userGroupList.value[user.type] = {
                total: currentDataList.total - 1,
                users: newUserList
            };
        }
    });

    /**
     * Mise à jour de la liste des utilisateurs
     * @param string userType
     * @param object newListData Données de la liste (total, items)
     * @return void
     */
    const updateUserList = ((userType, newListData) => {

        const currentData = getUserListDataOfType.value(userType);
        if(currentData.total === undefined) {
            userGroupList.value[userType] = {
                total: newListData.total,
                users: newListData.items
            };

            newListData.items.forEach((user) => {
                updateUser(user);
            });

        } else {
            userGroupList.value[userType].total = newListData.total;

            const currentUsers = userGroupList.value[userType].users;

            const newUsers = newListData.items.filter((item) => ! currentUsers.find((current) => current.id === item.id));
            userGroupList.value[userType].users = [ ...currentUsers, ...newUsers ];

            newUsers.forEach((user) => {
                updateUser(user);
            });
        }

    });

    /**
     * Réinitialisation de la liste des utilisateurs du type en paramètre
     * @param string userType
     * @return void
     */
    const resetUserList = ((userType) => {
        userGroupList.value[userType] = {};
    });

    return {
        getUserListDataOfType,
        loadUserListOfType,
        getUserById,
        loadUserById,
        updateUser,
        removeUser,
        addCredits,
        changeCredits,
        toggleAgentStatus
    };

}));
